import { Reducer, useContext } from 'react';
import { logError } from 'utils/datadog';
import {
  sendAbortCompleteShift,
  sendCompleteShift,
  sendElementTapped,
  sendInitAddShift,
  sendInitLogin,
  sendScreenViewed
} from '../events';
import {
  ADD_SHIFT_MODAL_SCREEN_NAME,
  HOMEPAGE_SCREEN_NAME,
  SHIFT_OVERVIEW_SCREEN_NAME,
  TIMESHEET_SECTION_NAME,
  UNAUTHORIZED_WELCOME_SECTION_NAME,
  WEEK_DETAILS_SCREEN_NAME,
  WEEKS_OVERVIEW_SCREEN_NAME
} from './constants';
import { TrackingDispatchContext } from './tracking-context';
import { TrackingAction, TrackingState } from './types';

export const useTrackingDispatch = (): React.Dispatch<TrackingAction> => {
  const context = useContext(TrackingDispatchContext);
  if (context === undefined) {
    throw new Error(
      'useShiftTrackingDispatch must be used within a AddShiftTrackingProvider'
    );
  }
  return context;
};

const handleScreenViewed = (
  screenName: string,
  sectionName: string,
  state: TrackingState,
  type: string
) => {
  sendScreenViewed({
    screen_name: screenName,
    section_name: sectionName
  });

  return {
    ...state,
    actions: [...state.actions, { type }]
  };
};

const handleAddShifModalElementTapped = (
  elementName: string,
  elementText: string,
  state: TrackingState,
  type: string
) => {
  if (!state.id || !state.weekId) {
    logError('No state id or weekId in state');
    return state;
  }

  sendElementTapped({
    add_shift_uuid: state.id,
    period_uuid: state.weekId,
    element_name: elementName,
    element_text: elementText,
    screen_name: ADD_SHIFT_MODAL_SCREEN_NAME,
    section_name: TIMESHEET_SECTION_NAME
  });

  return {
    ...state,
    actions: [...state.actions, { type }]
  };
};

export const trackingReducer: Reducer<
  TrackingState | undefined,
  TrackingAction
> = (
  state = {
    id: null,
    weekId: null,
    actions: []
  },
  action
) => {
  switch (action.type) {
    case 'INIT_SHIFT':
      sendInitAddShift({
        add_shift_uuid: action.payload.id,
        element_name: 'add_shift_button',
        element_text: 'add_shift',
        period_uuid: action.payload.weekId,
        section_name: TIMESHEET_SECTION_NAME,
        screen_name: WEEK_DETAILS_SCREEN_NAME
      });

      return {
        ...state,
        id: action.payload.id,
        weekId: action.payload.weekId,
        actions: [...state.actions, { type: action.type }]
      };

    case 'ABORT_SHIFT':
      if (!state.id || !state.weekId) {
        console.error('No state id or weekId in state');
        return state;
      }
      sendAbortCompleteShift({
        add_shift_uuid: state.id,
        element_name: 'cancel_add_shift_button',
        element_text: 'cancel_add_shift',
        period_uuid: state.weekId,
        section_name: TIMESHEET_SECTION_NAME,
        screen_name: ADD_SHIFT_MODAL_SCREEN_NAME
      });

      return {
        ...state,
        actions: [...state.actions, { type: action.type }]
      };

    case 'CONFIRM_SHIFT':
      if (!state.id || !state.weekId) {
        console.error('No state id or weekId in state');
        return state;
      }

      sendCompleteShift({
        add_shift_uuid: state.id,
        element_name: 'confirm_add_shift_button',
        element_text: 'confirm_add_shift',
        period_uuid: state.weekId,
        section_name: TIMESHEET_SECTION_NAME,
        screen_name: ADD_SHIFT_MODAL_SCREEN_NAME
      });

      return {
        ...state,
        actions: [...state.actions, { type: action.type }]
      };

    case 'OVERVIEW_PAGE_VIEWED':
      return handleScreenViewed(
        SHIFT_OVERVIEW_SCREEN_NAME,
        TIMESHEET_SECTION_NAME,
        state,
        action.type
      );

    case 'WEEK_DETAILS_PAGE_VIEWED':
      return handleScreenViewed(
        'week_details_page',
        TIMESHEET_SECTION_NAME,
        state,
        action.type
      );

    case 'WEEKS_OVERVIEW_PAGE_VIEWED':
      return handleScreenViewed(
        WEEKS_OVERVIEW_SCREEN_NAME,
        TIMESHEET_SECTION_NAME,
        state,
        action.type
      );

    case 'HOME_PAGE_VIEWED':
      return handleScreenViewed(
        HOMEPAGE_SCREEN_NAME,
        UNAUTHORIZED_WELCOME_SECTION_NAME,
        state,
        action.type
      );

    case 'ADD_BREAK_BUTTON_CLICKED':
      return handleAddShifModalElementTapped(
        'add_break_button',
        'add_break',
        state,
        action.type
      );

    case 'REMOVE_BREAK_BUTTON_CLICKED':
      return handleAddShifModalElementTapped(
        'remove_break_button',
        'remove_break',
        state,
        action.type
      );

    case 'ADD_COMMENT_BUTTON_CLICKED':
      return handleAddShifModalElementTapped(
        'add_comment_button',
        'add_comment',
        state,
        action.type
      );

    case 'REMOVE_COMMENT_BUTTON_CLICKED':
      return handleAddShifModalElementTapped(
        'remove_comment_button',
        'remove_comment',
        state,
        action.type
      );

    case 'WEEK_BUTTON_CLICKED':
      sendElementTapped({
        element_name: 'week_item_button',
        element_text: 'week_item',
        period_uuid: action.payload.weekId,
        screen_name: WEEKS_OVERVIEW_SCREEN_NAME,
        section_name: TIMESHEET_SECTION_NAME
      });

      return {
        ...state,
        weekId: action.payload.weekId,
        actions: [...state.actions, { type: action.type }]
      };

    case 'INIT_LOGIN':
      sendInitLogin({
        login_uuid: action.payload.id,
        login_type: action.payload.loginType,
        element_name: 'goto_login_page_button',
        element_text: 'continue',
        screen_name: HOMEPAGE_SCREEN_NAME,
        section_name: UNAUTHORIZED_WELCOME_SECTION_NAME
      });
      return {
        ...state,
        actions: [...state.actions, { type: action.type }]
      };

    default:
      return state;
  }
};
